import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaTimes, FaSearch, FaCar } from "react-icons/fa";
import AdminDocumentUploader from "../AdminDocumentUploader";

export default function AdminCustomCabModal({ onClose, onCreated }) {
  const BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";
  
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [services, setServices] = useState([]);
  
  // Step 1: Search Parameters
  const [searchParams, setSearchParams] = useState({
    userId: "",
    countryId: "",
    cityId: "",
    serviceTypeId: "",
  });

  // Step 2: Available Cabs
  const [availableCabs, setAvailableCabs] = useState([]);
  const [selectedCab, setSelectedCab] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);

  // Step 3: Booking Details
  const [bookingDetails, setBookingDetails] = useState({
    pickupAddress: "",
    dropAddress: "",
    pickupTime: "",
    totalKm: "",
    isPeak: false,
    isNight: false,
    totalAmount: "",
    status: "confirmed",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
  });

  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [newBookingId, setNewBookingId] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (searchParams.countryId) fetchCities(searchParams.countryId);
  }, [searchParams.countryId]);

  const fetchInitialData = async () => {
    try {
      const [usersRes, countriesRes, servicesRes] = await Promise.all([
        axios.get(`${BASE}/api/user?limit=1000`, { withCredentials: true }),
        axios.get(`${BASE}/api/cab/admin/countries`, { withCredentials: true }),
        axios.get(`${BASE}/api/cab/admin/services`, { withCredentials: true })
      ]);
      if (usersRes.data.success) setUsers(usersRes.data.data.users || usersRes.data.data || []);
      if (countriesRes.data.success) setCountries(countriesRes.data.data || []);
      if (servicesRes.data.success) setServices(servicesRes.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCities = async (countryId) => {
    try {
      const res = await axios.get(`${BASE}/api/cab/admin/cities`, { withCredentials: true });
      if (res.data.success) {
        setCities(res.data.data.filter(c => c.countryId === countryId || c.countryId?._id === countryId) || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({ ...prev, [name]: value }));
    setHasSearched(false);
    setSelectedCab(null);
    
    if (name === "userId" && value) {
      const selectedUser = users.find(u => u._id === value);
      if (selectedUser) {
        setBookingDetails(prev => ({
          ...prev,
          customerName: selectedUser.name || selectedUser.username || "",
          customerEmail: selectedUser.email || "",
          customerPhone: selectedUser.phone || "",
        }));
      }
    }
  };

  const handleDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBookingDetails(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCalculatePrice = async () => {
    if (!bookingDetails.totalKm || isNaN(bookingDetails.totalKm)) {
      return alert("Please enter a valid total distance in km.");
    }
    setCalculating(true);
    try {
      const payload = {
        serviceTypeId: searchParams.serviceTypeId,
        cabTypeId: selectedCab._id,
        countryId: searchParams.countryId,
        cityId: searchParams.cityId,
        totalKm: Number(bookingDetails.totalKm),
        isPeak: bookingDetails.isPeak,
        isNight: bookingDetails.isNight
      };
      const res = await axios.post(`${BASE}/api/cab/user/price`, payload, { withCredentials: true });
      if (res.data.success) {
        setBookingDetails(prev => ({ ...prev, totalAmount: res.data.data.totalAmount }));
      }
    } catch (err) {
      alert("Failed to calculate price. Make sure pricing rules exist for this route.");
    } finally {
      setCalculating(false);
    }
  };

  const handleSearchCabs = async () => {
    if (!searchParams.countryId || !searchParams.cityId || !searchParams.serviceTypeId) {
      return alert("Please select country, city, and service type.");
    }
    setSearching(true);
    try {
      const res = await axios.post(`${BASE}/api/cab/user/search`, searchParams, { withCredentials: true });
      if (res.data.success) {
        setAvailableCabs(res.data.data.cabTypes || []);
        setHasSearched(true);
      }
    } catch (err) {
      alert("Failed to search cabs");
    } finally {
      setSearching(false);
    }
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (!searchParams.userId || !selectedCab) return alert("Missing user or cab selection");
    try {
      setLoading(true);
      const selectedCity = cities.find(c => c._id === searchParams.cityId);
      
      const payload = {
        userId: searchParams.userId,
        customerName: bookingDetails.customerName,
        customerEmail: bookingDetails.customerEmail,
        customerPhone: bookingDetails.customerPhone,
        countryId: searchParams.countryId,
        cityId: searchParams.cityId,
        serviceTypeId: searchParams.serviceTypeId,
        cabTypeId: selectedCab._id,
        pickupAddress: bookingDetails.pickupAddress,
        dropAddress: bookingDetails.dropAddress,
        pickupTime: bookingDetails.pickupTime,
        totalAmount: bookingDetails.totalAmount,
        status: bookingDetails.status,
        
        // Custom cab details to bypass ID validations later
        pickupCity: selectedCity?.name || "N/A",
        dropCity: selectedCity?.name || "N/A",
        tripType: services.find(s => s._id === searchParams.serviceTypeId)?.name || "N/A",
        journeyDate: bookingDetails.pickupTime,
        vehicleType: selectedCab.name || "N/A"
      };

      const res = await axios.post(`${BASE}/api/cab/admin/custom-booking`, payload, { withCredentials: true });
      if (res.data.success) {
        setNewBookingId(res.data.data._id);
        alert("Custom cab booking created successfully!");
      }
    } catch (error) {
      alert(error.response?.data?.error || "Failed to create custom booking");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-gray-50 rounded-xl shadow-xl w-full max-w-4xl max-h-[95vh] overflow-y-auto relative p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800">
          <FaTimes size={20} />
        </button>
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Create Custom Cab Booking</h2>

        {!newBookingId ? (
          <div className="space-y-8">
            
            {/* Step 1: Search */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <h3 className="font-semibold text-lg mb-4 text-gray-700 border-b pb-2">1. Search Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Select User *</label>
                  <select name="userId" value={searchParams.userId} onChange={handleSearchChange} className="w-full border p-2 rounded">
                    <option value="">-- User --</option>
                    {users.map(u => (
                      <option key={u._id} value={u._id}>{u.username} ({u.email})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Country *</label>
                  <select name="countryId" value={searchParams.countryId} onChange={handleSearchChange} className="w-full border p-2 rounded">
                    <option value="">-- Country --</option>
                    {countries.map(c => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <select name="cityId" value={searchParams.cityId} onChange={handleSearchChange} className="w-full border p-2 rounded" disabled={!searchParams.countryId}>
                    <option value="">-- City --</option>
                    {cities.map(c => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Service Type *</label>
                  <select name="serviceTypeId" value={searchParams.serviceTypeId} onChange={handleSearchChange} className="w-full border p-2 rounded">
                    <option value="">-- Service --</option>
                    {services.map(s => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button onClick={handleSearchCabs} disabled={searching} className="bg-blue-600 text-white px-6 py-2 rounded font-bold hover:bg-blue-700 flex items-center gap-2">
                  <FaSearch /> {searching ? "Searching..." : "Search Cabs"}
                </button>
              </div>
            </div>

            {/* Step 2: Cab Selection */}
            {hasSearched && (
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h3 className="font-semibold text-lg mb-4 text-gray-700 border-b pb-2">2. Available Cab Options</h3>
                {availableCabs.length === 0 ? (
                  <p className="text-red-500">No cabs available for this route/service.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {availableCabs.map(cab => (
                      <div 
                        key={cab._id} 
                        onClick={() => setSelectedCab(cab)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${selectedCab?._id === cab._id ? 'border-blue-600 bg-blue-50 shadow-md ring-2 ring-blue-200' : 'hover:border-blue-400'}`}
                      >
                        <div className="flex items-center gap-3">
                          <FaCar className="text-blue-500 text-2xl" />
                          <div>
                            <h4 className="font-bold text-gray-900">{cab.name || "Unknown Vehicle"}</h4>
                            {cab.description && <p className="text-sm text-gray-600">{cab.description}</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Booking Details Form */}
            {selectedCab && (
              <form onSubmit={handleSubmitBooking} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 space-y-4">
                <h3 className="font-semibold text-lg mb-4 text-gray-700 border-b pb-2">3. Booking Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Customer Name *</label>
                      <input type="text" required name="customerName" value={bookingDetails.customerName} onChange={handleDetailsChange} className="w-full border p-2 rounded text-sm" placeholder="Full Name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Customer Email *</label>
                      <input type="email" required name="customerEmail" value={bookingDetails.customerEmail} onChange={handleDetailsChange} className="w-full border p-2 rounded text-sm" placeholder="Email Address" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Customer Phone *</label>
                      <input type="text" required name="customerPhone" value={bookingDetails.customerPhone} onChange={handleDetailsChange} className="w-full border p-2 rounded text-sm" placeholder="Phone Number" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Pickup Address *</label>
                    <input required type="text" name="pickupAddress" value={bookingDetails.pickupAddress} onChange={handleDetailsChange} className="w-full border p-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Drop Address *</label>
                    <input required type="text" name="dropAddress" value={bookingDetails.dropAddress} onChange={handleDetailsChange} className="w-full border p-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Pickup Date & Time *</label>
                    <input required type="datetime-local" name="pickupTime" value={bookingDetails.pickupTime} onChange={handleDetailsChange} className="w-full border p-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Total Distance (km) *</label>
                    <input required type="number" name="totalKm" value={bookingDetails.totalKm} onChange={handleDetailsChange} className="w-full border p-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select name="status" value={bookingDetails.status} onChange={handleDetailsChange} className="w-full border p-2 rounded">
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  
                  <div className="md:col-span-2 flex items-center gap-6 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" name="isPeak" checked={bookingDetails.isPeak} onChange={handleDetailsChange} className="w-4 h-4 text-blue-600 rounded" />
                      <span className="text-sm font-medium text-gray-700">Apply Peak Hours Multiplier</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" name="isNight" checked={bookingDetails.isNight} onChange={handleDetailsChange} className="w-4 h-4 text-blue-600 rounded" />
                      <span className="text-sm font-medium text-gray-700">Apply Night Charges Multiplier</span>
                    </label>
                  </div>

                  <div className="md:col-span-2 flex justify-end">
                    <button type="button" onClick={handleCalculatePrice} disabled={calculating} className="bg-gray-800 text-white px-4 py-2 rounded font-bold hover:bg-gray-900 shadow text-sm">
                      {calculating ? "Calculating..." : "Calculate Final Fare"}
                    </button>
                  </div>

                  <div className="md:col-span-2 bg-blue-50 p-4 rounded-lg flex items-center justify-between border border-blue-100">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Final Amount (Auto-Calculated or Admin Override)</p>
                      <p className="text-xs text-gray-500">Calculate fare above, or manually enter the final amount.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold">₹</span>
                      <input required type="number" name="totalAmount" value={bookingDetails.totalAmount} onChange={handleDetailsChange} className="w-40 border p-2 rounded font-bold text-lg text-blue-700" placeholder="0.00" />
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button type="submit" disabled={loading} className="w-full bg-green-600 text-white p-3 rounded-lg font-bold text-lg hover:bg-green-700 shadow-md">
                    {loading ? "Processing..." : "Confirm Booking"}
                  </button>
                </div>
              </form>
            )}

          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 text-green-800 rounded">
              Cab Booking Created Successfully! ID: {newBookingId}
            </div>
            <AdminDocumentUploader
              type="cab"
              bookingId={newBookingId}
              onUploadSuccess={(docs) => {}}
            />
            <button onClick={() => { onCreated(); onClose(); }} className="w-full bg-gray-200 text-gray-800 p-2 rounded font-bold hover:bg-gray-300 mt-4">
              Finish & Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
